import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem, useTheme, Button } from '@mui/material';
import IntroForm from './components/IntroForm';
import Domains from './components/Domains';
import { loadQuestions } from './services/questionService';
import { AssessmentMetadata } from './types';
import Questionnaire from './components/Questionnaire';
import Results from './components/Results';
import Footer from './components/Footer';
import MenuIcon from '@mui/icons-material/Menu';

interface NavigationProps {
  metadata: AssessmentMetadata | null;
  title: string;
}

const Navigation: React.FC<NavigationProps> = ({ metadata, title }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const toIntro = () => {
    console.log('WA DEBUG: to intro');
    navigate('/intro');
    handleMenuClose();
  }
  const toDomain = () => {
    console.log('WA DEBUG: to domains');
    navigate('/domains');
    handleMenuClose();
  }

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: 'primary.main',
        borderBottom: `1px solid ${theme.palette.primary.dark}`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ display: { xs: 'block', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <img
            src="/appvia.png"
            alt="Appvia Logo"
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.5px',
              fontSize: '1.5rem',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
            <Button
              color="inherit"
              onClick={() => toIntro()}
              sx={{ textTransform: 'none' }}
            >
              Intro
            </Button>
            <Button
              color="inherit"
              onClick={() => toDomain()}
              sx={{ textTransform: 'none' }}
            >
              Domains
            </Button>
          </Box>

          {/* {metadata && (
            <Typography
              variant="body2"
              sx={{
                display: { xs: 'none', sm: 'block' },
                ml: 2,
                borderLeft: `1px solid ${theme.palette.primary.light}`,
                pl: 2
              }}
            >
              {metadata.teamName}
            </Typography>
          )} */}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          sx={{ mt: 1 }}
        >
          <MenuItem onClick={() => handleNavigation('/intro')}>Intro</MenuItem>
          <MenuItem onClick={() => handleNavigation('/domains')}>Domains</MenuItem>
          {metadata && (
            <MenuItem onClick={() => handleNavigation('/questionnaire')}>Assessment</MenuItem>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

const AppContent: React.FC = () => {
  const [assessmentMetadata, setAssessmentMetadata] = useState<AssessmentMetadata | null>(null);
  const [assessmentTitle, setAssessmentTitle] = useState<string>("Assessment");

  useEffect(() => {
    const fetchAssessmentData = async () => {
      try {
        const data = await loadQuestions();

        setAssessmentTitle(data.name);
        document.title = data.name;
      } catch (error) {
        console.error('Failed to load assessment data:', error);
      }
    };

    fetchAssessmentData();
  }, []);

  const handleMetadataSubmit = (metadata: AssessmentMetadata) => {
    setAssessmentMetadata(metadata);
  };

  return (
    <ThemeProvider theme={createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#1976d2',
          light: '#42a5f5',
          dark: '#1565c0',
          contrastText: '#ffffff'
        },
        secondary: {
          main: '#dc004e',
        },
      },
    })}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Navigation
          metadata={assessmentMetadata}
          title={assessmentTitle}
        />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route
              path="/"
              element={
                assessmentMetadata ? <Navigate to="/domains" replace /> : <IntroForm onSubmit={handleMetadataSubmit} />
              }
            />
            <Route
              path="/intro"
              element={
                assessmentMetadata ? <Navigate to="/intro" replace /> : <IntroForm onSubmit={handleMetadataSubmit} />
              }
            />
            <Route
              path="/domains"
              element={
                <Domains />
              }
            />
            <Route path="/questionnaire" element={<Questionnaire />} />
            <Route path="/results" element={<Results />} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App; 